import type { TFunction } from "i18next";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { deleteGroup, readAccessList } from "../../../api/access/accessApi";
import type { AccessList, ListedGroup } from "../../../api/access/accessModel";
import {
  exclusionChange,
  GroupList,
  groupNamer,
  memberAdded,
  NewGroupDialog,
  PeopleList,
  PersonPanel,
  RequestsToDecide,
  useAccessChanges,
  type AccessChange,
  type GroupNamer,
} from "../../../features/access";
import { ACCESS_PERSON_PARAMETER, appPaths } from "../../../shared/routing/appPaths";
import { useLoad } from "../../../shared/loading/useLoad";
import AppButton from "../../../ui/buttons/AppButton";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import SegmentedControl from "../../../ui/navigation/SegmentedControl";
import ConfirmAction from "../../../ui/overlays/ConfirmAction";
import type { MenuItem } from "../../../ui/overlays/MenuButton";
import { classNames } from "../../../ui/styles/classNames";
import AddMemberField from "../members/AddMemberField";
import { useProjectSettings } from "../projectSettingsContext";
import NewGroupButton from "./NewGroupButton";

/**
 * Project › Access (redesign R3-3 `access`, accounts.md GS-1 to GS-8): the requests one may
 * grant on top, then the project's groups in their order beside its people; one click on a
 * person opens their access panel. On a
 * narrow screen a Groups / People switch keeps both in one column (PV3-7).
 */
export default function ProjectAccessPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { overview, permissionsChanged } = useProjectSettings();
  const projectId = overview.project.projectId;
  const [params, setParams] = useSearchParams();
  const personId = params.get(ACCESS_PERSON_PARAMETER);
  const load = useCallback((signal: AbortSignal) => readAccessList(projectId, signal), [projectId]);
  const { state, reload, refresh } = useLoad(load);
  const changed = useCallback(() => { refresh(); permissionsChanged(); }, [refresh, permissionsChanged]);
  const changes = useAccessChanges(changed, state);
  // On a narrow screen one column shows: People whenever a person's panel is open, by a click or by the address.
  const [narrowView, setNarrowView] = useState<"groups" | "people">(personId === null ? "groups" : "people");
  const [shownPerson, setShownPerson] = useState(personId);
  if (shownPerson !== personId) {
    setShownPerson(personId);
    if (personId !== null) setNarrowView("people");
  }
  const [creating, setCreating] = useState(false);
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState<ListedGroup | null>(null);

  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  const list = state.value;
  const namer = groupNamer(t, list);
  const mayManage = list.givable.size > 0;
  const openPerson = (userId: string | null) => {
    setNarrowView("people");
    setParams(userId === null ? {} : { [ACCESS_PERSON_PARAMETER]: userId });
  };
  // Every group whose members the server lets the viewer change: the project's own, then the installation groups it includes.
  const addable = [...list.groups.filter((group) => group.projectId !== null), ...list.groups.filter((group) => group.projectId === null)]
    .filter((group) => group.membersManageable);

  return (
    <div className="space-y-4">
      <RequestsToDecide projectId={projectId} namer={namer} onGranted={changed} />
      <SegmentedControl<"groups" | "people">
        className="lg:hidden"
        label={t("access.view")}
        fill={true}
        selected={narrowView}
        options={[{ key: "groups", label: t("access.groups") }, { key: "people", label: t("access.people") }]}
        onSelect={setNarrowView}
      />
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className={classNames(narrowView === "people" && "hidden lg:block")}>
          <GroupList
            list={list}
            namer={namer}
            groupPath={(groupId) => appPaths.projectSettingsGroup(projectId, groupId)}
            changes={changes}
            action={mayManage ? <NewGroupButton list={list} namer={namer} changes={changes} onNew={() => { setCreating(true); }} /> : undefined}
            menuItems={(group) => removalItems(t, group, list, namer, () => { setDeleting(group); }, (item) => { changes.run(item); })}
          />
        </div>
        <div className={classNames(narrowView === "groups" && "hidden lg:block")}>
          {personId === null ? (
            <PeopleList list={list} namer={namer} onOpen={openPerson}
              action={addable.length > 0 ? (
                <AppButton variant="primary" size="sm" aria-expanded={adding} onClick={() => { setAdding(!adding); }}>{t("access.addPeople")}</AppButton>
              ) : undefined}>
              {adding ? (
                <div className="px-4 pb-3">
                  <AddMemberField
                    groups={addable.map((group) => ({ groupId: group.groupId, name: namer.name(group.groupId), installation: group.projectId === null }))}
                    defaultGroupId={addable.find((group) => group.system === "contributors")?.groupId ?? addable[0]?.groupId ?? ""}
                    onAddToGroup={(groupId, person) => { changes.run(memberAdded(t, { groupId, name: namer.name(groupId) }, { userId: person.userId, name: person.displayName })); }}
                  />
                </div>
              ) : null}
            </PeopleList>
          ) : (
            <PersonPanel key={personId} projectId={projectId} userId={personId} list={list} changes={changes} onClose={() => { openPerson(null); }} />
          )}
        </div>
      </div>
      {changes.feedback}
      {creating ? (
        <NewGroupDialog list={list} namer={namer} onClose={() => { setCreating(false); }}
          onCreated={(groupId) => { changed(); void navigate(appPaths.projectSettingsGroup(projectId, groupId)); }} />
      ) : null}
      {deleting === null ? null : (
        <ConfirmAction title={t("access.deleteTitle", { group: namer.name(deleting.groupId) })} confirmLabel={t("access.delete")} tone="danger"
          onConfirm={async () => { await deleteGroup(deleting.groupId); changed(); }} onClose={() => { setDeleting(null); }}>
          <p>{t("access.deleteExplained")}</p>
        </ConfirmAction>
      )}
    </div>
  );
}

/** A group's removal from its menu: delete one of the project's own groups, or stop including an installation group. */
function removalItems(
  t: TFunction,
  group: ListedGroup,
  list: AccessList,
  namer: GroupNamer,
  onDelete: () => void,
  run: (change: AccessChange) => void,
): readonly MenuItem[] {
  if (!group.manageable || group.system !== null) return [];
  if (group.projectId === null && list.projectId !== null) {
    const projectId = list.projectId;
    const included = { groupId: group.groupId, name: namer.name(group.groupId), grants: group.grants };
    const order = list.groups.map((listed) => listed.groupId);
    return [{ id: "exclude", label: t("access.removeHere"), action: { kind: "select", onSelect: () => { run(exclusionChange(t, projectId, included, group.overrides, order)); } } }];
  }
  return [{ id: "delete", label: t("access.delete"), action: { kind: "select", onSelect: onDelete } }];
}
